/**
 * useNavGroups — builds navigation groups ADDITIVELY from TOOLS_MANIFEST.
 *
 * Redesign Phase 2.1 (WorkspaceShell).
 *
 * Principles:
 * - Additive filtering: include tools where roles includes current user role
 * - NO NAV_HIDDEN subtraction logic — that was the old way
 * - Role-flavored group labels: attorney sees 'Generators', admin sees 'Operations'
 * - Group by NavGroup field from manifest
 */

import { useMemo } from 'react';
import {
  TOOLS_MANIFEST,
  ToolManifestEntry,
  WorkspaceRole,
  NavGroup,
} from '../../config/toolsManifest';

export type { WorkspaceRole, NavGroup } from '../../config/toolsManifest';

export interface NavItem {
  id: string;
  label: string;
  route: string | null;
  icon?: string;
  badge?: number;
  needsCase?: boolean;
}

export interface NavGroupData {
  id: NavGroup;
  label: string;
  items: NavItem[];
}

/**
 * Map NavGroup to default label.
 */
const DEFAULT_GROUP_LABELS: Record<NavGroup, string> = {
  work_desk: 'Work Desk',
  cases: 'Cases',
  review: 'Review',
  deliverables: 'Deliverables',
  intelligence: 'Intelligence',
  admin: 'Admin',
};

/**
 * Role-flavored group label overrides.
 * These override the default labels for specific role + NavGroup combinations.
 */
const ROLE_GROUP_LABEL_OVERRIDES: Partial<Record<WorkspaceRole, Partial<Record<NavGroup, string>>>> = {
  attorney: {
    work_desk: 'Generators',
    cases: 'Matters',
  },
  admin: {
    work_desk: 'Operations',
    admin: 'System',
  },
};

/**
 * Get the display label for a NavGroup based on user role.
 */
function getGroupLabel(group: NavGroup, role: WorkspaceRole): string {
  const roleOverrides = ROLE_GROUP_LABEL_OVERRIDES[role];
  if (roleOverrides && roleOverrides[group]) {
    return roleOverrides[group]!;
  }
  return DEFAULT_GROUP_LABELS[group];
}

/**
 * Filter manifest entries by role (additive filtering).
 * Returns entries where the entry's roles array includes the user's role.
 */
function filterByRole(entries: ToolManifestEntry[], role: WorkspaceRole): ToolManifestEntry[] {
  return entries.filter((entry) => entry.roles.includes(role));
}

/**
 * Admin-rail allowlist (admin dashboard spec §2/§10/§11): the admin seat is
 * firm-first and deliberately short — firm → case → seat replaces the portfolio
 * of work-desk / queue / case-scoped links. Rather than strip 'admin' from ~26
 * shared manifest entries (which risks other roles), we keep the admin rail to
 * this allowlist centrally. Other roles are completely unaffected.
 *
 * Operations: Firms, Golden Cases, All Users, Analytics.
 * System: Settings, Activity Log, Fleet HUD (admin-only).
 * (Leads / Team Members / Feature Flags / Data Health / Error Logs are Settings
 * tabs today; promoting them into the System group is the next slice.)
 */
const ADMIN_RAIL_ALLOWLIST = new Set<string>([
  'admin-firms',
  'admin-golden-cases',
  'admin-all-users',
  'analytics',
  // System group (spec §9)
  'settings',
  'admin-leads',
  'admin-team-members',
  'admin-feature-flags',
  'admin-data-health',
  'admin-error-logs',
  'activity-log',
  'fleet-hud',
]);

/**
 * Group manifest entries by their NavGroup.
 */
function groupByNavGroup(entries: ToolManifestEntry[]): Map<NavGroup, ToolManifestEntry[]> {
  const groups = new Map<NavGroup, ToolManifestEntry[]>();

  entries.forEach((entry) => {
    const existing = groups.get(entry.group) || [];
    existing.push(entry);
    groups.set(entry.group, existing);
  });

  return groups;
}

/**
 * Convert ToolManifestEntry to NavItem.
 */
function toNavItem(entry: ToolManifestEntry): NavItem {
  return {
    id: entry.id,
    label: entry.label,
    route: entry.route,
    needsCase: entry.needsCase,
  };
}

export interface UseNavGroupsOptions {
  role: WorkspaceRole;
  /** If true, filter out entries that require a case when no case is open */
  hideNeedsCase?: boolean;
  /** Optional badge counts keyed by tool id */
  badgeCounts?: Record<string, number>;
}

export interface UseNavGroupsResult {
  groups: NavGroupData[];
  /** Flat list of all nav items (for command palette, etc) */
  allItems: NavItem[];
  /** Find a nav item by id */
  findById: (id: string) => NavItem | undefined;
  /** Find nav items by route */
  findByRoute: (route: string) => NavItem[];
}

/**
 * Hook to build navigation groups from TOOLS_MANIFEST.
 *
 * @example
 * const { groups, allItems } = useNavGroups({ role: 'attorney' });
 */
export function useNavGroups(options: UseNavGroupsOptions): UseNavGroupsResult {
  const { role, hideNeedsCase = false, badgeCounts = {} } = options;

  return useMemo(() => {
    // 1. Filter manifest by role (additive filtering)
    let entries = filterByRole(TOOLS_MANIFEST, role);

    // 1b. Admin seat is firm-first + deliberately short (spec §2/§10/§11).
    if (role === 'admin') {
      entries = entries.filter((entry) => ADMIN_RAIL_ALLOWLIST.has(entry.id));
    }

    // 2. Filter out entries that require a case if requested
    if (hideNeedsCase) {
      entries = entries.filter((entry) => !entry.needsCase);
    }

    // 3. Filter out palette-only entries from nav (they're in ⌘K only)
    entries = entries.filter((entry) => !entry.paletteOnly);

    // 4. Group by NavGroup
    const grouped = groupByNavGroup(entries);

    // 5. Build NavGroupData array (maintaining consistent group order)
    const groupOrder: NavGroup[] = ['work_desk', 'cases', 'review', 'deliverables', 'intelligence', 'admin'];
    const groups: NavGroupData[] = groupOrder
      .filter((groupId) => grouped.has(groupId))
      .map((groupId) => {
        const groupEntries = grouped.get(groupId) || [];
        const items = groupEntries.map((entry) => ({
          ...toNavItem(entry),
          badge: badgeCounts[entry.id],
        }));

        return {
          id: groupId,
          label: getGroupLabel(groupId, role),
          items,
        };
      });

    // 6. Build flat list of all items
    const allItems = groups.flatMap((g) => g.items);

    // 7. Utility functions
    const findById = (id: string): NavItem | undefined => allItems.find((item) => item.id === id);

    const findByRoute = (route: string): NavItem[] =>
      allItems.filter((item) => item.route === route || (item.route && route.startsWith(item.route.replace(/:\w+/g, ''))));

    return {
      groups,
      allItems,
      findById,
      findByRoute,
    };
  }, [role, hideNeedsCase, badgeCounts]);
}

export default useNavGroups;
